import React from 'react'

import {
  Box,
  Icon,
  Text,
  Tooltip,
  useClassNames,
  useToggle,
} from '@factorialco/deprecated-design-system'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import QuestionIcon from 'modules/questionnaires/components/QuestionIcon'
import {
  QuestionTypeSelector as QuestionTypeSelectorType,
  Translations,
} from 'modules/questionnaires/lib/types'

import * as styles from './index.vanilla.css'

interface FilterOption {
  label: string
  value: QuestionTypeSelectorType
}
type TypeTranslations = Pick<Translations['question'], 'type_selector' | 'types'>

const RatingToolTip: React.FC = () => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt
      attachment="bottom center"
      open={open}
      toggle={toggle}
      label={i18n.t('questionnaires.rating_tooltip.description')}
    >
      <Icon.Medium icon="info-circle-line" />
    </Tooltip.Alt>
  )
}

export type Props = {
  isOpen?: boolean
  error?: string
  selection?: QuestionTypeSelectorType
  placeholder?: string
  translations: TypeTranslations
  disabled?: boolean
  onToggle?: (isOpen: boolean) => void
  onSelection: (type: QuestionTypeSelectorType) => void
}

const QuestionTypeSelector: React.FC<Props> = ({
  isOpen,
  selection = 'text',
  translations,
  disabled = false,
  onToggle,
  onSelection,
  error,
  placeholder,
}) => {
  const cx = useClassNames(styles)
  const [open, setOpen] = React.useState(isOpen)

  const options: FilterOption[] = [
    {
      label: translations.types.text,
      value: 'text',
    },
    {
      label: i18n.t('questionnaires.question_types.number'),
      value: 'number',
    },
    {
      label: translations.types.rating,
      value: 'rating',
    },
    {
      label: translations.types.single_choice,
      value: 'single_choice',
    },
    {
      label: translations.types.multiple_choice,
      value: 'multiple_choice',
    },
  ]

  const valueLabel =
    options.find((option) => option.value === String(selection))?.label ?? placeholder

  const isQuestionType = (type: unknown): type is QuestionTypeSelectorType => {
    return (
      typeof type === 'string' &&
      (type === 'text' ||
        type === 'rating' ||
        type === 'number' ||
        type === 'multiple_choice' ||
        type === 'single_choice')
    )
  }

  const handleSelection = (type: string) => {
    if (isQuestionType(type)) {
      onSelection(type)
    }
  }

  const handleToggle = (value: boolean) => {
    if (onToggle) onToggle(value)
    setOpen(value)
  }

  useEffect(() => {
    if (disabled) return

    setOpen(isOpen)
  }, [isOpen, disabled])

  return (
    <Box width="full">
      <PopoverMenu type="list" onClick={handleToggle} open={open}>
        <PopoverMenu.Trigger disabled={disabled}>
          <LabeledInput
            value={selection}
            readonly={disabled}
            error={error}
            label={translations.type_selector.label}
            secondaryLabel={selection === 'rating' && <RatingToolTip />}
          >
            <Box
              paddingX="s16"
              paddingY="s8"
              flexDirection="row"
              height="s40"
              alignItems="center"
              width="full"
              cursor="pointer"
            >
              <Box flexDirection="row" width="full" gap="s6">
                <Box overflowX="hidden" textAlign="left">
                  <Text
                    size="300"
                    color={!!selection ? 'grey1000' : 'grey600'}
                    overflowWrap
                    ellipsis
                  >
                    {valueLabel}
                  </Text>
                </Box>
              </Box>
              <Icon.Small icon="caret-down" />
            </Box>
          </LabeledInput>
        </PopoverMenu.Trigger>

        <PopoverMenu.Content side="bottom" align="start" width="s400">
          <Box className={styles.content}>
            {options.map((option) => (
              <DropdownMenu.Item
                key={option.value}
                className={cx('item', 'layout.full', {
                  disabled,
                })}
                disabled={disabled}
                onSelect={() => handleSelection(option.value)}
              >
                <Box flexDirection="row" gap="s16" alignItems="center">
                  <Box flexDirection="row" gap="s12" alignItems="center" flexGrow>
                    <QuestionIcon type={option.value} />
                    <Text.Normal
                      size="300"
                      weight="medium"
                      color={disabled ? 'grey600' : 'grey800'}
                    >
                      {option.label}
                    </Text.Normal>
                  </Box>
                  {selection === option.value && (
                    <Box>
                      <Icon.Medium icon="check" color="viridian1000" />
                    </Box>
                  )}
                </Box>
              </DropdownMenu.Item>
            ))}
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default QuestionTypeSelector
